/* NIR (numéro de sécurité sociale) — client-side validation + step gating.
 *
 * The SAME predicate (shouldCollectNir) gates the progress-bar spine
 * (helper/inscriptionSteps.ts) AND the real navigation (HomeContent
 * handleNextInscription) so the two can never desynchronize.
 *
 * The mod-97 check MUST stay in sync with the server's
 * kastafiorementalplus-api/src/helpers/nirControlKey.ts — same leading-digit
 * policy ([1278]: 1/2 definitive, 7/8 provisional NIA; historical 3/4 numbers
 * are rejected pending an operator policy decision), same Corsica rule.
 * A passing key is a TYPO FILTER only — it never proves the number belongs
 * to the declarant.
 */
import { AttributeEnum, isPasswordSignInMethod, NirCollectionModeType, ProfileType } from "../types/profile";
import { RoleEnum } from "../types/invitation.types";

export const NIR_LENGTH = 15;

/** Strip user formatting (spaces, dots, dashes) and upcase Corsica letters. */
export const normalizeNir = (raw?: string): string =>
  (raw ?? "").replace(/[\s.-]/g, "").toUpperCase();

/**
 * Control key of a 13-char NIR body. Corsica: the département occupies body
 * indexes 5–6; '2A' → letter becomes '0' then subtract 1 000 000 before the
 * modulo, '2B' → '0' and 2 000 000 (INSEE rule). Département 99 (born abroad)
 * is plain digits. The 13-digit body (≤ 9.9e12) is exact in a JS Number.
 * Returns null when the body is not a valid NIR body.
 */
export const computeNirKey = (body: string): number | null => {
  if (body.length !== 13) return null;
  let digits = body;
  let corsicaOffset = 0;
  if (body[6] === "A") {
    digits = body.slice(0, 6) + "0" + body.slice(7);
    corsicaOffset = 1_000_000;
  } else if (body[6] === "B") {
    digits = body.slice(0, 6) + "0" + body.slice(7);
    corsicaOffset = 2_000_000;
  }
  if (!/^\d{13}$/.test(digits)) return null;
  return 97 - ((Number(digits) - corsicaOffset) % 97);
};

/** Full client-side NIR check (15 chars after normalization, mod-97 key). */
export const isValidNir = (raw?: string): boolean => {
  const nir = normalizeNir(raw);
  if (nir.length !== NIR_LENGTH) return false;
  if (!/^\d{5}(?:\d{2}|2A|2B)\d{6}\d{2}$/.test(nir)) return false;
  if (!/^[1278]/.test(nir)) return false;
  const expected = computeNirKey(nir.slice(0, 13));
  return expected !== null && expected === Number(nir.slice(13));
};

/** Effective collection mode for a role (absent/unknown ⇒ HIDDEN, fail-safe). */
export const getNirCollectionMode = (role?: ProfileType): NirCollectionModeType => {
  const mode = role?.attributes?.nir_collection_mode;
  return mode === "OPTIONAL" || mode === "REQUIRED" ? mode : "HIDDEN";
};

/**
 * The NIR step only renders for a self-registering, password-authenticated
 * SUBJECT (the parent declares their OWN number). Mirrors the Diapason
 * control's gating and defends against a stale REQUIRED left on a role later
 * switched to no-signin / pre-created / secret-questions.
 */
export const shouldCollectNir = (role?: ProfileType): boolean =>
  role?.name === RoleEnum.SUBJECT &&
  role?.attributes?.[AttributeEnum.IS_AUTHENTICATIVE] !== false &&
  isPasswordSignInMethod(role?.access?.signInMode) &&
  getNirCollectionMode(role) !== "HIDDEN";

/**
 * The role also links the NIR holder's OWN care (e.g. the mother's), so the NIR
 * step also asks the holder's date de naissance — the one triplet datum the NIR
 * does not embed. The server checks it strictly when sent for such a role (it is
 * optional there: without it the holder's row carries the NIR alone) and ignores
 * it for any other role.
 */
export const shouldAskNirHolderBirthdate = (role?: ProfileType): boolean =>
  shouldCollectNir(role) && role?.attributes?.nir_link_holder_care === true;

/**
 * Typo catcher: the NIR embeds the birth year (chars 2-3) and month (chars 4-5);
 * a declared YYYY-MM-DD date must agree. Pseudo-months (20, 30-42, 50+: unknown
 * or estimated dates) skip the check. Keep in sync with the server's
 * kastafiorementalplus-api/src/helpers/nirControlKey.ts isBirthdateConsistentWithNir.
 */
export const isBirthdateConsistentWithNir = (rawNir: string, isoBirthdate: string): boolean => {
  const nir = normalizeNir(rawNir);
  const date = String(isoBirthdate).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || nir.length < 5) return true;
  const month = Number(nir.slice(3, 5));
  if (!(month >= 1 && month <= 12)) return true;
  return date.slice(2, 4) === nir.slice(1, 3) && date.slice(5, 7) === nir.slice(3, 5);
};

/**
 * A real calendar day (YYYY-MM-DD, round-trips), from 1900, not in the future —
 * the same rule the server applies before storing the holder's date de naissance.
 */
export const isPlausibleBirthdate = (isoBirthdate: string): boolean => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(isoBirthdate).slice(0, 10));
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day && year >= 1900 && date.getTime() <= Date.now();
};
